import { classes } from 'html-classes';
import type React from 'react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Icon from '~/components/Icon/Icon';
import { Button } from '~/components/isolated/Button';
import type { Promocode } from '~/entities/Promocode/Promocode';
import { useDateLang } from '~/hooks/useDateLang';
import { usePromocode } from '~/hooks/usePromocode';
import copyToClipboard from '~/utils/copyToClipboard';

import promocodeImage from '../../assets/images/promocode.png';

import styles from './PromocodeCard.module.scss';

interface PromocodeCardProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  promocode: Promocode;
}

const PromocodeCard: React.FC<PromocodeCardProps> = ({
  promocode,
  ...props
}) => {
  const { locale } = useDateLang();

  const [copied, setCopied] = useState(false);

  const copyTimeoutRef = useRef<Nullable<ReturnType<typeof setTimeout>>>(null);

  const { t } = useTranslation();

  const { checkPromocode, setPromocodeVal } = usePromocode({
    checkPercentageType: true,
    watchPaidWithDiscount: true,
  });

  const {
    allowedCategories,
    isActive,
    isInactive,
    inactiveReason,
    code,
    description,
    title,
    usageLimitCustomerDescription,
    titleWithOrder,
  } = promocode;

  const orderDate = useMemo(
    () => promocode.getDateWithOrder(locale),
    [promocode, locale],
  );

  const validUntil = useMemo(
    () => promocode.getValidUntil(locale),
    [promocode, locale],
  );

  const handleApply = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    copyToClipboard({
      text: code,
      successMessage: t('promocodeApplied'),
    });
    setCopied(true);

    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);

    setPromocodeVal(code);
    checkPromocode(code);
  };

  return (
    <div
      {...props}
      className={classes([styles.promocodeCard, props.className])}
    >
      {promocode.isNew && (
        <span className={styles.promocodeCardNewBadge}>{t('new')}</span>
      )}
      <img
        src={promocodeImage}
        alt="Promocode"
        className={classes([
          styles.promocodeCardImage,
          !isActive && styles.promocodeCardImageInactive,
        ])}
      />

      <div className={styles.promocodeCardContent}>
        <div className={styles.promocodeCardContentHeader}>
          <div className={styles.promocodeCardContentHeaderTitle}>
            <h4 className={styles.promocodeCardTitle}>
              {titleWithOrder ?? title}
            </h4>
            {orderDate && (
              <span className={styles.promocodeCardDate}>{orderDate}</span>
            )}
          </div>
          {titleWithOrder && (
            <p className={styles.promocodeCardDescription}>{title}</p>
          )}
          {description && (
            <p className={styles.promocodeCardDescription}>{description}</p>
          )}
          {isActive && allowedCategories.length > 0 && (
            <div className={styles.promocodeCardCategories}>
              {allowedCategories.map((category) => (
                <Link
                  key={category.id}
                  to={`/c/${category.slug}?showProducts=true`}
                  className={styles.promocodeCardCategory}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          )}
          {usageLimitCustomerDescription && (
            <p className={styles.promocodeCardDescription}>
              {usageLimitCustomerDescription}
            </p>
          )}
        </div>
        {isActive && (
          <Button
            variant="secondary"
            size="M"
            className={styles.promocodeCardCodeButton}
            onClick={handleApply}
          >
            {code}
            <Icon type={copied ? 'check' : 'copy-promocode'} />
          </Button>
        )}
      </div>

      {((isInactive && inactiveReason) || (isActive && validUntil)) && (
        <div className={styles.promocodeBadge}>
          {isInactive ? inactiveReason : validUntil}
        </div>
      )}
    </div>
  );
};

export default PromocodeCard;
